import React, { ReactElement, ReactNode, useContext } from 'react';
import styled from 'styled-components';
import { AlertType, IJobInfo, IJobProgress, Maybe } from '../../graphql/schema/p4types';
import {
  Link,
  makeStyles,
  ProgressBar,
  Tag,
  Toast,
  ToastBody,
  ToastFooter,
  ToastTitle,
  ToastTrigger,
  useToastController,
} from '@fluentui/react-components';
import AppContext from '../Context/AppContext';
import { SentryUserFeedbackModal } from '../Sentry/SentryUserFeedbackModal';
import Icon from '@mdi/react';
import { mdiPencil } from '@mdi/js';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import { useFullUserDetails } from '../../utils/hooks';

const useStyles = makeStyles({
  footer: {
    flexWrap: 'wrap',
    rowGap: '3px',
  },
  hidden: {
    display: 'none',
  },
});

export const NotificationMessageList = styled.ul`
  padding-left: 1.2em;
  line-height: 1.2;
`;

export const NotificationMessageSingle = styled.div`
  line-height: 1.2;
`;

const NotificationProgressBar = ({ jobProgress, type }: { jobProgress: IJobProgress; type?: AlertType }) => {
  if (!jobProgress) return null;

  if (!jobProgress.recordCount || jobProgress.recordCount === 0 || type === AlertType.IndeterminateProgress) {
    return (
      <div style={{ paddingTop: '5px' }}>
        <ProgressBar />
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '5px' }}>
      <ProgressBar value={jobProgress.recordsProcessed} max={jobProgress.recordCount} />
    </div>
  );
};

//Display nested job progress information to an arbitrary depth.
//(Limited by how deep the .graphql has fetched the job information, of course).
const NestedJobProgressInformation = ({ job }: { job: Maybe<IJobInfo | IJobProgress> }) => {
  if (job?.childProgress?.message) {
    return (
      <div style={{ paddingTop: '10px' }}>
        {job.childProgress.message}
        <NotificationProgressBar jobProgress={job.childProgress} />
        <NestedJobProgressInformation job={job.childProgress} />
      </div>
    );
  }
  return null;
};

export type INotificationProps = {
  type: AlertType;
  title: string | undefined; //If undefined, will display as 'Status' (Only expected scenario is job progress notifications).
  message?: Maybe<string>;
  job?: any;
  actionButtons?: Array<ReactElement>;
  errorId?: string;
};

const Notification = ({ type, title, message: initialMessage, job, actionButtons, errorId }: INotificationProps) => {
  const styles = useStyles();

  const { toasterId } = useContext(AppContext);
  const fullUserDetails = useFullUserDetails();
  const { dismissAllToasts } = useToastController(toasterId);

  const { dispatchSimpleToast } = useP4ToastController();

  const message = job?.message ?? initialMessage ?? 'Unknown';

  const messages: ReactNode[] = [];
  const splitMessage = message?.split('\n') ?? [];
  for (let idx = 0; idx < splitMessage.length; idx++) {
    const msg = splitMessage[idx];
    if (idx > 10) {
      messages.push(<li key={'additional'}>+ {splitMessage.length - 10} additional messages</li>);
      break;
    }
    messages.push(<li key={msg}>{msg}</li>);
  }

  const regex = /\$COPYABLE\((.*?)\)/g;
  const copyableMessage: (string | JSX.Element)[] = [];
  let lastIndex = 0;

  if (message) {
    for (const match of message.matchAll(regex)) {
      if (match.index && match.index > lastIndex) {
        copyableMessage.push(message.slice(lastIndex, match.index));
      }

      copyableMessage.push(
        <Tag
          key={match.index}
          onClick={() => {
            dispatchSimpleToast('Text copied', 'Text copied to clipboard', AlertType.Success);
            navigator.clipboard.writeText(match[1]);
          }}
          title="Click to copy"
          appearance={'filled'}
          style={{
            cursor: 'pointer',
          }}
          size={'small'}
          shape={'rounded'}
          icon={<Icon path={mdiPencil} />}
        >
          {match[1]}
        </Tag>
      );

      if (match.index) {
        lastIndex = match.index + match[0].length;
      }
    }

    if (lastIndex < message.length) {
      copyableMessage.push(message.slice(lastIndex));
    }
  }

  return (
    <Toast data-testid={`notification-${type}`}>
      <ToastTitle
        data-testid="notification-title"
        action={
          <ToastTrigger>
            <Link
              onClick={(e) => {
                if (e.shiftKey) {
                  dismissAllToasts();
                }
              }}
            >
              Dismiss
            </Link>
          </ToastTrigger>
        }
      >
        {title ?? 'Status'}
      </ToastTitle>
      <ToastBody
        style={{
          overflow: 'auto',
          maxWidth: '400px',
          wordWrap: 'break-word',
          whiteSpace: 'normal',
        }}
      >
        {messages.length > 1 ? (
          <NotificationMessageList>{messages}</NotificationMessageList>
        ) : (
          <NotificationMessageSingle>{copyableMessage}</NotificationMessageSingle>
        )}
        <NotificationProgressBar jobProgress={job} type={type} />
        <NestedJobProgressInformation job={job} />
      </ToastBody>
      {actionButtons ? (
        <ToastFooter className={styles.footer}>
          {actionButtons}
          {errorId && <SentryUserFeedbackModal errorId={errorId} />}
        </ToastFooter>
      ) : null}
    </Toast>
  );
};

export default Notification;
